import React, { useState, useEffect } from 'react'
import { FaUser, FaEnvelope, FaMobile, FaLock, FaEdit, FaSave, FaTimes } from 'react-icons/fa'
import { useAuth } from '../context/AuthContext'
import authService from '../services/authService'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import SEO from '../utils/SEO'
import Button from '../components/ui/Button'

const Profile = () => {
  const { user, login } = useAuth()
  const [isEditing, setIsEditing] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showPasswordForm, setShowPasswordForm] = useState(false)

  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    mobile_number: '',
  })

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        email: user.email || '',
        mobile_number: user.mobile_number || '',
      })
    }
  }, [user])

  const handleProfileChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value,
    })
  }

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value,
    })
  }

  const handleProfileUpdate = async (e) => {
    e.preventDefault()

    if (!profileData.name || !profileData.mobile_number) {
      toast.error('Name and mobile number are required')
      return
    }

    try {
      setLoading(true)
      const response = await authService.updateProfile(profileData)

      if (response.success) {
        // Update local storage and context
        const updatedUser = response.user
        localStorage.setItem('user', JSON.stringify(updatedUser))

        // Re-login to update context
        window.location.reload()

        toast.success('Profile updated successfully!')
        setIsEditing(false)
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Error updating profile'
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordUpdate = async (e) => {
    e.preventDefault()

    if (
      !passwordData.currentPassword ||
      !passwordData.newPassword ||
      !passwordData.confirmPassword
    ) {
      toast.error('All password fields are required')
      return
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match')
      return
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters')
      return
    }

    try {
      setLoading(true)
      const response = await authService.updatePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      })

      if (response.success) {
        toast.success('Password updated successfully!')
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        })
        setShowPasswordForm(false)
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Error updating password'
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleCancelEdit = () => {
    setProfileData({
      name: user.name || '',
      email: user.email || '',
      mobile_number: user.mobile_number || '',
    })
    setIsEditing(false)
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <SEO title="My Profile" noindex />
      <ToastContainer />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-display font-bold text-gray-900 mb-4">My Profile</h1>
          <p className="text-gray-600 text-lg">Manage your account information</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Card */}
          <div className="lg:col-span-1">
            <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-6 text-center">
              <div className="w-32 h-32 mx-auto mb-4 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center text-white text-5xl font-bold">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">{user?.name}</h2>
              <p className="text-gray-600 mb-2">{user?.email || 'No email'}</p>
              <p className="text-gray-600 mb-4">{user?.mobile_number}</p>
              <span
                className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                  user?.role === 'admin'
                    ? 'bg-purple-100 text-purple-800'
                    : 'bg-green-100 text-green-800'
                }`}
              >
                {user?.role === 'admin' ? 'Admin' : 'Customer'}
              </span>
            </div>
          </div>

          {/* Profile Details & Edit Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Personal Information */}
            <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">Personal Information</h3>
                {!isEditing ? (
                  <Button variant="secondary" onClick={() => setIsEditing(true)} icon={<FaEdit />}>
                    Edit Profile
                  </Button>
                ) : (
                  <Button variant="ghost" size="sm" icon={<FaTimes />} onClick={handleCancelEdit}>
                    Cancel
                  </Button>
                )}
              </div>

              {isEditing ? (
                <form onSubmit={handleProfileUpdate} className="space-y-4">
                  <div>
                    <label className="label">
                      <FaUser className="inline mr-2" />
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={profileData.name}
                      onChange={handleProfileChange}
                      className="input-field"
                      required
                    />
                  </div>

                  <div>
                    <label className="label">
                      <FaEnvelope className="inline mr-2" />
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={profileData.email}
                      onChange={handleProfileChange}
                      className="input-field"
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div>
                    <label className="label">
                      <FaMobile className="inline mr-2" />
                      Mobile Number
                    </label>
                    <input
                      type="tel"
                      name="mobile_number"
                      value={profileData.mobile_number}
                      onChange={handleProfileChange}
                      className="input-field"
                      required
                    />
                  </div>

                  <div className="flex gap-4 pt-4">
                    <Button
                      type="submit"
                      loading={loading}
                      icon={!loading ? <FaSave /> : undefined}
                      className="flex-1"
                    >
                      Save Changes
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-start">
                    <FaUser className="text-gray-400 mt-1 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Full Name</p>
                      <p className="text-gray-900 font-semibold">{user?.name}</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <FaEnvelope className="text-gray-400 mt-1 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Email Address</p>
                      <p className="text-gray-900 font-semibold">{user?.email || 'Not provided'}</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <FaMobile className="text-gray-400 mt-1 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Mobile Number</p>
                      <p className="text-gray-900 font-semibold">{user?.mobile_number}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Password Change */}
            {user?.auth_provider === 'local' && (
              <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Security</h3>
                  {!showPasswordForm && (
                    <Button
                      variant="secondary"
                      onClick={() => setShowPasswordForm(true)}
                      icon={<FaLock />}
                    >
                      Change Password
                    </Button>
                  )}
                </div>

                {showPasswordForm ? (
                  <form onSubmit={handlePasswordUpdate} className="space-y-4">
                    <div>
                      <label className="label">Current Password</label>
                      <input
                        type="password"
                        name="currentPassword"
                        value={passwordData.currentPassword}
                        onChange={handlePasswordChange}
                        className="input-field"
                        required
                      />
                    </div>

                    <div>
                      <label className="label">New Password</label>
                      <input
                        type="password"
                        name="newPassword"
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        className="input-field"
                        required
                      />
                    </div>

                    <div>
                      <label className="label">Confirm New Password</label>
                      <input
                        type="password"
                        name="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        className="input-field"
                        required
                      />
                    </div>

                    <div className="flex gap-4 pt-4">
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setShowPasswordForm(false)
                          setPasswordData({
                            currentPassword: '',
                            newPassword: '',
                            confirmPassword: '',
                          })
                        }}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        loading={loading}
                        icon={!loading ? <FaSave /> : undefined}
                        className="flex-1"
                      >
                        Update Password
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className="flex items-start">
                    <FaLock className="text-gray-400 mt-1 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Password</p>
                      <p className="text-gray-900 font-semibold">••••••••</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Account Type Info */}
            <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Account Information</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Account Type</span>
                  <span className="font-semibold text-gray-900">
                    {user?.role === 'admin' ? 'Administrator' : 'Customer'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Authentication Method</span>
                  <span className="font-semibold text-gray-900 capitalize">
                    {user?.auth_provider || 'Local'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Account Status</span>
                  <span className="inline-block px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
                    Active
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Profile
